// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
// --- Hooks & Store
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useDateMathCalculationsActions } from 'datastage/store/dateMathCalculations/useDateMathCalculations';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Grid Utils
import { useDateCalculationsGridUtils } from './utils/gridUtils';
// --- Components
import { DateMathCalculationsSearchForm, fetchDateMathCalculations } from '../search-form';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Date Math Calculations Grid
 *
 * Features:
 * - Displays a data grid of date math calculations using AG Grid
 * - Integrates with Redux for date math calculations state management
 * - Handles deletion of individual date math calculations
 *
 * @returns {Element}
 * @constructor
 */
export const DateMathCalculationsGrid = () => {
    // Custom hooks and actions
    const agGridApi = useAgGridApi();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { calculations, filterForm, rangeParam, sortParam, defaultFilterRange } = useSelector(
        (s) => s.dateMathCalculations,
    );
    const { updateDateMathCalculations } = useDateMathCalculationsActions();

    // Utilize custom hook for grid utilities
    const { setSort, columns } = useDateCalculationsGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateDateMathCalculations,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => onDeleteDateMathCalculation(data),
            ]
        }
    });


    // Handle deletion of a date math calculation
    const onDeleteDateMathCalculation = async (calc) => {
        setLoadingTrue();
        try {
            const res = await datastageApi.deleteDateMathCalculation(calc?.id);
            if (res?.code) {
                showErrorToast(res?.message || 'Failed to delete calculation.');
                return;
            }

            const data = await fetchDateMathCalculations(
                filterCriteria(filterForm, '', 'dateMathCalculations'),
                rangeParam,
                sortParam
            );
            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch calculations after deletion.');
                return;
            }

            updateDateMathCalculations({
                calculations: data?.content,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                totalItems: data?.totalElements,
            });

            showSuccessToast('Calculation deleted successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Failed to delete calculation.');
            console.error('Error deleting calculation:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    return (
        <>
            <DateMathCalculationsSearchForm />
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: calculations,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Date Math Calculations Found'}
            />
        </>
    );
};
